import { gql, useLazyQuery, useQuery } from "@apollo/client";
import { useFlags } from "launchdarkly-react-client-sdk";
import { functions, isEqual, omit } from "lodash";
import React, { Dispatch, FC, memo, SetStateAction, useContext, useEffect, useMemo, useRef, useState } from "react";
import { AppText, FlexDiv, SkeletonBlock } from "src/Components/UI";
import { PhoenixIcon } from "src/Components/UI/Phoenix";
import { CallContext, LeadCardContext, ModalContext } from "src/context";
import { edit, google, pin } from "src/images/NewDesign";
import { useLocalStoragePersistedState, useSessionStoragePersistedState } from "src/utils/hooks";
import { theme } from "src/utils/theme";
import styled from "styled-components";
import { SequenceTag } from "../CompactLeadCard";
import { SearchMenuV2 } from "../SearchMenuV2";
import { ContactTab } from "./ContactTab";
import { HistoryTab } from "./HistoryTab";
import { MoreInfoTab } from "./MoreInfoTab";
import { NotesTab } from "./NotesTab";
import { RelatedRecordsTab } from "./RelatedRecordsTab";

const FETCH_LEAD = gql`
  query fetchLead($id: String) {
    fetchLead(id: $id) {
      id
      favorited
      primary_phone_number
      conference_number
      current_lead_type
      city
      country
      lead_value {
        value
      }
      timezone_by_state
      lead_source
      industry
      sub_industry
      address
      channel
      business_name
      full_name
      first_name
      last_name
      state
      rep_id
      lead_ownership_status
      computed_mrr
      current_close_date
      is_hot

      primary_email
      primary_email_title
      primary_email_type

      primary_phone_number_country_code
      local_primary_phone_number
      primary_phone_number_title
      primary_phone_number_type

      alternate_contacts {
        id
        title
        channel
        type
        value
        mapping_order
        contact_label
        country_code
      }

      flagged_numbers: LeadFlaggedNumber {
        id
        phone_number
        lead_activity {
          related_disposition {
            id
            type
          }
        }
      }

      next_intent_scheduled_or_unscheduled {
        id
        type
        replacement_lead_intent_id
      }

      next_lead_intent {
        id
        schedule_item {
          id
          deleted_at
        }
      }

      sequence_id
      sequence_step_id
      sequence_step {
        actions {
          id
          task
          custom_task_note
          scheduled_event_behavior
        }
      }

      visible_custom_fields {
        id
        key
        value
        visible
        boo_value
        num_value
        date_value
        type
        lead_id
        options
        computed_value
        list_value
        allow_reps_to_edit
      }

      last_lead_activity {
        id
        type
        created_at
      }

      brand {
        id
      }

      organization {
        id
        name
        allow_remove_step_action
        allow_snooze_step_action
      }

      email_sub_status
      sms_sub_status
    }
  }
`;
const FETCH_ASSOCIATED_LEADS = gql`
  query fetchAssociateLeads($primaryLeadId: String!) {
    fetchAssociateContact(primary_lead_id: $primaryLeadId) {
      id
      business_name
      first_name
      last_name
      full_name
      city
      rep_id

      conference_number
      primary_phone_number
      primary_phone_number_country_code
      primary_phone_number_title
      primary_phone_number_type
      local_primary_phone_number

      primary_email
      primary_email_title
      primary_email_type

      lead_ownership_status

      alternate_contacts {
        id
        title
        channel
        type
        value
        mapping_order
        contact_label
        country_code
      }
    }
  }
`;
const FETCH_NOTE_HISTORY = gql`
  query fetchNoteHistory($lead_id: String!, $event_filter: HistoryEventTypeOption, $show_all_lead: Boolean) {
    fetchLeadHistory(lead_id: $lead_id, event_filter: $event_filter, show_all_lead: $show_all_lead) {
      id
      created_at
      notes
      html
      user {
        id
        full_name
        phone_number
        profile_image
      }
      is_pinned_to_top
    }
  }
`;

interface IDetailedLeadCard {
  intentData?: any;
}

const DetailedLeadCard: FC<IDetailedLeadCard> = ({ intentData }) => {
  const { showPinnedQueue } = useFlags();

  const { setEditModal, setEditModalLeadId, setShowAddOrEditContactModal } = useContext(ModalContext);
  const { selectedLeadId } = useContext(CallContext);
  const { favoriteLead, unfavoriteLead, selectedTabDialQueue } = useContext(LeadCardContext);

  const leadId: any = useMemo(() => {
    return selectedLeadId;
  }, [selectedLeadId]);

  const [selectedTab, setSelectedTab] = useLocalStoragePersistedState<
    "History" | "Contact" | "Notes" | "More Info" | "Related Records"
  >("selectedTab", "Contact");

  const [contactFilter, setContactFilter] = useSessionStoragePersistedState<string | undefined>(
    "contactFilter",
    undefined,
  ) as [string | undefined, Dispatch<SetStateAction<string | undefined>>];

  const [showSearchMenu, setShowSearchMenu] = useState(false);

  const ref = useRef<HTMLDivElement>(null);
  const [bodyHeight, setBodyHeight] = useState(0);
  useEffect(() => {
    if (ref.current) {
      // container height minus header height
      setBodyHeight(ref.current.clientHeight - 80);
    }
  }, [ref.current]);

  const [fetchLead, { data, loading: loadingLead }] = useLazyQuery(FETCH_LEAD, {
    fetchPolicy: "cache-and-network",
    variables: {
      id: leadId,
    },
  });
  useEffect(() => {
    if (!leadId) return;
    fetchLead();
  }, [leadId]);

  const [fetchNoteHistory, { data: noteHistoryData }] = useLazyQuery(FETCH_NOTE_HISTORY, {
    fetchPolicy: "cache-and-network",
    variables: {
      lead_id: contactFilter ?? leadId,
      event_filter: "Note",
      show_all_lead: !contactFilter,
    },
  });
  const { data: dataAssociated } = useQuery(FETCH_ASSOCIATED_LEADS, {
    variables: {
      primaryLeadId: leadId,
    },
    skip: !leadId,
    fetchPolicy: "network-only",
  });

  const lead = useMemo(() => data?.fetchLead || intentData?.lead, [data, intentData]);

  const handleFavoriteLead = (isFavorite: boolean) => {
    if (isFavorite) {
      unfavoriteLead(lead?.id);
    } else {
      favoriteLead(lead?.id);
    }
  };

  return (
    <Container ref={ref}>
      {showSearchMenu && (
        <SearchMenuV2
          setIsSearchClicked={setShowSearchMenu}
          businessName={lead?.business_name}
          fullName={lead?.full_name}
          city={lead?.city}
          right="38px"
        />
      )}

      <Header>
        <FlexDiv align="center" justify="space-between" padding="8px 8px 8px 24px">
          {!!lead ? (
            <FlexDiv align="center" gap={8}>
              {showPinnedQueue && (
                <PhoenixIcon
                  svg={pin}
                  size={16}
                  color={lead?.favorited ? theme.icon.brand.default : theme.icon.neutral.default}
                  hoverColor={lead?.favorited ? theme.icon.brand.default : theme.icon.neutral.default}
                  pointer
                  onClick={() => {
                    handleFavoriteLead(!!lead?.favorited);
                  }}
                />
              )}
              <FlexDiv>
                <LeadIdentifierText
                  fontSize={16}
                  fontWeight={500}
                  lineHeight={22}
                  key={lead?.business_name}
                  onClick={() => window.open(`/lead-detail/${lead?.id}`, "_blank", "noreferrer")}
                >
                  {lead?.business_name || "N/A"}
                </LeadIdentifierText>
              </FlexDiv>
            </FlexDiv>
          ) : (
            <SkeletonBlock width={180} height={22} borderRadius={4} />
          )}

          <FlexDiv align="center" gap={8}>
            <PhoenixIcon
              svg={edit}
              size={16}
              pointer
              onClick={() => {
                setEditModal(true);
                setEditModalLeadId(leadId);
              }}
            />
            <img
              src={google}
              style={{ width: "22px", height: "22px", cursor: "pointer" }}
              loading="lazy"
              alt={`google ${lead?.business_name || "NA"}`}
              onClick={() => setShowSearchMenu(true)}
            />
            {lead?.next_intent_scheduled_or_unscheduled?.type === "SequenceAction" && <SequenceTag />}
          </FlexDiv>
        </FlexDiv>
        <HeaderTabContainer>
          <HeaderTabBorderFill width={16} />
          <Tab
            selected={selectedTab === "History"}
            tabName="History"
            onClick={() => {
              setSelectedTab("History");
            }}
          />
          <Tab
            selected={selectedTab === "Contact"}
            tabName="Contact"
            onClick={() => {
              setSelectedTab("Contact");
            }}
          />
          <Tab
            selected={selectedTab === "Notes"}
            tabName="Notes"
            onClick={() => {
              setSelectedTab("Notes");
              !noteHistoryData && !!leadId && fetchNoteHistory();
            }}
          />
          <Tab
            selected={selectedTab === "More Info"}
            tabName="More Info"
            onClick={() => {
              setSelectedTab("More Info");
            }}
          />
          <Tab
            selected={selectedTab === "Related Records"}
            tabName="Related Records"
            onClick={() => {
              setSelectedTab("Related Records");
            }}
          />
          <HeaderTabBorderFill />
        </HeaderTabContainer>
      </Header>

      <Body>
        {selectedTab === "History" && <HistoryTab leadId={leadId} maxHeight={bodyHeight} />}
        {selectedTab === "Contact" && (
          <ContactTab
            leadData={lead}
            intentData={intentData}
            maxHeight={bodyHeight}
            associatedContacts={dataAssociated?.fetchAssociateContact}
            loadingLead={loadingLead}
            fetchLead={fetchLead}
          />
        )}
        {selectedTab === "Notes" && (
          <NotesTab
            leadData={lead}
            associatedContacts={dataAssociated?.fetchAssociateContact}
            noteHistoryData={noteHistoryData?.fetchLeadHistory}
            maxHeight={bodyHeight}
            contactFilter={contactFilter}
            setContactFilter={setContactFilter}
          />
        )}
        {selectedTab === "More Info" && <MoreInfoTab leadData={lead} maxHeight={bodyHeight} />}
        {selectedTab === "Related Records" && <RelatedRecordsTab maxHeight={bodyHeight} leadId={lead?.id} />}
      </Body>
    </Container>
  );
};

const Container = styled.div`
  position: relative;

  width: 100%;
  height: 100%;

  border: 1px solid ${theme.border.neutral.primary};
  border-radius: 8px;

  overflow: hidden;
  background-color: ${theme.surface.brand.primary};
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;

  width: 100%;
  height: 80px;

  background-color: ${theme.fill.neutral.primary};
`;

const Body = styled.div`
  padding-top: 2px;
`;

const LeadIdentifierText = styled(AppText)`
  border-bottom: none;

  :hover {
    cursor: pointer;
    color: ${theme.text.brand.primary};
  }
  transition: color 0.2s ease-in-out;

  animation: ${theme.fadeIn} 0.5s ease forwards;
`;

const HeaderTabContainer = styled.div`
  display: flex;
  padding-top: 10px;
`;
const HeaderTabBorderFill = styled.div<{ width?: number }>`
  width: ${(props) => (props.width ? `${props.width}px` : "100%")};
  min-width: ${(props) => (props.width ? `${props.width}px` : "initial")};
  border-bottom: 1px solid ${theme.border.neutral.primary};
`;

interface ITab {
  selected: boolean;
  tabName: string;
  onClick: () => void;
}

const Tab: FC<ITab> = ({ selected, tabName, onClick }) => {
  return (
    <TabContainer selected={selected} onClick={onClick}>
      <AppText fontSize={10} fontWeight={600} lineHeight={16} uppercase letterSpacing={1} noWrap>
        {tabName}
      </AppText>
    </TabContainer>
  );
};
const TabContainer = styled.div<{ selected: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;

  width: fit-content;
  height: 32px;
  padding: 8px 16px;

  & ${AppText} {
    user-select: none;
    color: ${({ selected }) => (selected ? theme.text.neutral.primary : theme.text.neutral.secondary)};
    transition: color 0.2s ease-in-out;
  }

  border-bottom: ${({ selected }) => (selected ? "2px" : "1px")} solid
    ${({ selected }) => (selected ? theme.border.brand.primary : theme.border.neutral.primary)};

  cursor: pointer;

  transition: border-bottom 0.1s ease-in-out;
`;

const MemoizedDetailedLeadCard = memo(DetailedLeadCard, (oldProps, newProps) => {
  const oldP = omit(oldProps, functions(oldProps));
  const newP = omit(newProps, functions(newProps));
  return isEqual(oldP, newP);
});

export { MemoizedDetailedLeadCard as DetailedLeadCard };
